import React from "react";
import { PluginStore } from "graylog-web-plugin/plugin";
import { Trans, useTranslation } from "react-i18next";
import i18n from "i18n";
import { defaultCompare as naturalSort } from "logic/DefaultCompare";
import usePluginEntities from "hooks/usePluginEntities";
import usePluggableLicenseCheck from "hooks/usePluggableLicenseCheck";
import { Alert } from "components/bootstrap";
import { MarkdownPreview } from "components/common/MarkdownEditor";
import notificationTypeTitle from "components/event-notifications/notificationTypeTitle";
import commonStyles from "../common/commonStyles.css";
import { eventDefinitionTypeTitle } from "../pluginTitles";
import formatDuration from "../formatDuration";
const getPlugin = (name, type) => {
  if (type === void 0) {
    return void 0;
  }
  return PluginStore.exports(name).find((edt) => edt.type === type);
};
export function useEventProcedureSummaryComponents({
  eventProcedureId = void 0,
  remediationSteps = void 0
}) {
  const { t } = useTranslation("event_definitions");
  const pluggableEventProcedureSummary = usePluginEntities("views.components.eventProcedureSummary");
  const {
    data: { valid: validSecurityLicense }
  } = usePluggableLicenseCheck("/license/security");
  let label = null;
  let Component = null;
  if (validSecurityLicense) {
    label = t("summary_renders.event_procedure_summary");
    if (eventProcedureId) {
      const first = pluggableEventProcedureSummary == null ? void 0 : pluggableEventProcedureSummary[0];
      const PluggableEventProcedureSummary = first == null ? void 0 : first.component;
      Component = PluggableEventProcedureSummary ? /* @__PURE__ */ React.createElement(PluggableEventProcedureSummary, { eventProcedureId, key: first == null ? void 0 : first.key }) : /* @__PURE__ */ React.createElement("p", null, t("summary_renders.event_procedure_summary_unavailable"));
    } else {
      Component = /* @__PURE__ */ React.createElement("p", null, t("summary_renders.no_event_procedures"));
    }
  } else {
    label = t("summary_renders.remediation_steps");
    Component = /* @__PURE__ */ React.createElement(
      MarkdownPreview,
      {
        show: true,
        withFullView: true,
        noBorder: true,
        noBackground: true,
        value: remediationSteps || t("summary_renders.no_remediation_steps")
      }
    );
  }
  return {
    label,
    Component
  };
}
export function renderCondition(eventDefinition, definitionId, currentUser) {
  const { config, _scope } = eventDefinition;
  const conditionPlugin = getPlugin("eventDefinitionTypes", config.type);
  const component = (conditionPlugin == null ? void 0 : conditionPlugin.summaryComponent) ? React.createElement(conditionPlugin.summaryComponent, {
    config,
    currentUser,
    definitionId,
    entityScope: _scope
  }) : /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(
    Trans,
    {
      i18n,
      ns: "event_definitions",
      i18nKey: "summary_renders.condition_plugin_no_summary",
      values: { type: config.type },
      components: { em: /* @__PURE__ */ React.createElement("em", null) }
    }
  ));
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("h3", { className: commonStyles.title }, eventDefinitionTypeTitle(conditionPlugin) || config.type), component);
}
export function renderField(fieldName, config, keys, currentUser) {
  if (!config.providers || config.providers.length === 0) {
    return /* @__PURE__ */ React.createElement("span", { key: fieldName }, i18n.t("summary_renders.no_field_value_provider", { ns: "event_definitions" }));
  }
  const provider = config.providers[0];
  const fieldProviderPlugin = getPlugin("fieldValueProviders", provider == null ? void 0 : provider.type);
  return (fieldProviderPlugin == null ? void 0 : fieldProviderPlugin.summaryComponent) ? React.createElement(fieldProviderPlugin.summaryComponent, {
    fieldName,
    config,
    keys,
    key: fieldName,
    currentUser
  }) : /* @__PURE__ */ React.createElement("p", { key: fieldName }, /* @__PURE__ */ React.createElement(
    Trans,
    {
      i18n,
      ns: "event_definitions",
      i18nKey: "summary_renders.provider_plugin_no_summary",
      values: { type: provider.type },
      components: { em: /* @__PURE__ */ React.createElement("em", null) }
    }
  ));
}
function renderFieldList(fieldNames, fields, keys, currentUser) {
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("dl", null, /* @__PURE__ */ React.createElement("dt", null, i18n.t("summary_renders.keys", { ns: "event_definitions" })), /* @__PURE__ */ React.createElement("dd", null, keys.length > 0 ? keys.join(", ") : i18n.t("summary_renders.no_keys", { ns: "event_definitions" }))), fieldNames.sort(naturalSort).map((fieldName) => renderField(fieldName, fields[fieldName], keys, currentUser)));
}
export function renderFields(fields, keys, currentUser) {
  const fieldNames = Object.keys(fields);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("h3", { className: commonStyles.title }, i18n.t("summary_renders.fields", { ns: "event_definitions" })), fieldNames.length === 0 ? /* @__PURE__ */ React.createElement("p", null, i18n.t("summary_renders.no_fields", { ns: "event_definitions" })) : renderFieldList(fieldNames, fields, keys, currentUser));
}
function renderNotification(definitionNotification, notifications) {
  const notification = notifications.find((n) => n.id === definitionNotification.notification_id);
  let content = null;
  if (notification) {
    const notificationPlugin = getPlugin("eventNotificationTypes", notification.config.type);
    content = notificationPlugin.summaryComponent ? React.createElement(notificationPlugin.summaryComponent, {
      type: notificationTypeTitle(notificationPlugin),
      notification,
      definitionNotification
    }) : /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(
      Trans,
      {
        i18n,
        ns: "event_definitions",
        i18nKey: "summary_renders.notification_plugin_no_summary",
        values: { type: notification.config.type },
        components: { em: /* @__PURE__ */ React.createElement("em", null) }
      }
    ));
  } else {
    content = /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(
      Trans,
      {
        i18n,
        ns: "event_definitions",
        i18nKey: "summary_renders.notification_not_found",
        values: { id: definitionNotification.notification_id },
        components: { em: /* @__PURE__ */ React.createElement("em", null) }
      }
    ));
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, { key: definitionNotification.notification_id }, content);
}
function renderNotificationSettings(notificationSettings) {
  const formattedDuration = formatDuration(notificationSettings.grace_period_ms);
  const formattedGracePeriod = notificationSettings.grace_period_ms ? i18n.t("summary_renders.grace_period_set", { ns: "event_definitions", duration: formattedDuration }) : i18n.t("summary_renders.grace_period_disabled", { ns: "event_definitions" });
  const formattedBacklogSize = notificationSettings.backlog_size ? i18n.t("summary_renders.backlog_included", { ns: "event_definitions", count: notificationSettings.backlog_size }) : i18n.t("summary_renders.backlog_none", { ns: "event_definitions" });
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("h4", null, i18n.t("summary_renders.settings", { ns: "event_definitions" })), /* @__PURE__ */ React.createElement("dl", null, /* @__PURE__ */ React.createElement("dd", null, formattedGracePeriod), /* @__PURE__ */ React.createElement("dd", null, formattedBacklogSize)));
}
export function renderNotifications(definitionNotifications, notificationSettings, notifications, isPermitted) {
  const effectiveDefinitionNotifications = definitionNotifications.filter(
    (n) => isPermitted(`eventnotifications:read:${n.notification_id}`)
  );
  const notificationsWithMissingPermissions = definitionNotifications.filter(
    (n) => !effectiveDefinitionNotifications.map((nObj) => nObj.notification_id).includes(n.notification_id)
  );
  const warning = notificationsWithMissingPermissions.length > 0 ? /* @__PURE__ */ React.createElement(Alert, { bsStyle: "warning" }, i18n.t("summary_renders.missing_notification_permissions", { ns: "event_definitions" }), /* @__PURE__ */ React.createElement("br", null), notificationsWithMissingPermissions.map((n) => n.notification_id).join(", ")) : null;
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("h3", { className: commonStyles.title }, i18n.t("summary_renders.notifications", { ns: "event_definitions" })), /* @__PURE__ */ React.createElement("span", null, warning), effectiveDefinitionNotifications.length === 0 && notificationsWithMissingPermissions.length <= 0 ? /* @__PURE__ */ React.createElement("p", null, i18n.t("summary_renders.no_notifications", { ns: "event_definitions" })) : /* @__PURE__ */ React.createElement(React.Fragment, null, renderNotificationSettings(notificationSettings), definitionNotifications.map((notification) => renderNotification(notification, notifications))));
}
export function useTechniquesSummary(eventDefinition) {
  var _a, _b;
  const tacticsTechniquesSummaryPlugin = usePluginEntities("eventDefinitions.components.tacticsTechniquesSummary")[0];
  const tacticsTechniquesSummaryEnabled = (_b = (_a = tacticsTechniquesSummaryPlugin == null ? void 0 : tacticsTechniquesSummaryPlugin.useCondition) == null ? void 0 : _a.call(tacticsTechniquesSummaryPlugin)) != null ? _b : !!tacticsTechniquesSummaryPlugin;
  return tacticsTechniquesSummaryEnabled && tacticsTechniquesSummaryPlugin ? /* @__PURE__ */ React.createElement(tacticsTechniquesSummaryPlugin.component, { eventDefinition }) : null;
}
